import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileCode2, ExternalLink, AlertTriangle, Search, Loader2, BookPlus } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

const SUPPORT_LABEL = {
  code_supported: { text: 'Verified in code', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30' },
  needs_publication: { text: 'Requires literature reference', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30' },
  needs_validation_study: { text: 'Requires validation study', cls: 'bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30' },
};

export const CodeClaimSourceDialog = ({ open, onOpenChange, userId, codeProjectId, claim, usage = [], onChanged, onOpenChapter }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pubmed, setPubmed] = useState(null);
  const [searching, setSearching] = useState(false);
  const [attaching, setAttaching] = useState('');

  useEffect(() => {
    if (!open || !claim) return;
    setLoading(true); setError(''); setData(null); setPubmed(null);
    thesisApi.getCodeClaimSource(userId, codeProjectId, claim.id)
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || 'Could not load the source location.'))
      .finally(() => setLoading(false));
  }, [open, claim, userId, codeProjectId]);

  const findReference = async () => {
    setSearching(true);
    try {
      const res = await thesisApi.findCodeClaimReference(userId, codeProjectId, claim.id);
      setPubmed(res);
      if (!res.count) toast.error('PubMed returned no results for this statement');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'PubMed search failed');
    } finally {
      setSearching(false);
    }
  };

  const attach = async (pmid) => {
    setAttaching(pmid);
    try {
      const res = await thesisApi.attachCodeClaimReference(userId, codeProjectId, claim.id, { pmid });
      toast.success(res.message);
      onChanged?.();
      if (res.replaced) onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not attach the reference');
    } finally {
      setAttaching('');
    }
  };

  const support = SUPPORT_LABEL[claim?.support_type] || SUPPORT_LABEL.code_supported;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[95vw] sm:w-full max-h-[88vh] overflow-y-auto" data-testid="code-claim-source-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileCode2 className="w-4 h-4" />
            Where does this statement come from?
          </DialogTitle>
          <DialogDescription>
            Every technical statement is anchored to a file and line range in the indexed code snapshot.
          </DialogDescription>
        </DialogHeader>

        {claim && (
          <div className="space-y-3 min-w-0">
            <div className="rounded-lg border border-border p-3 bg-muted/30 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Badge variant="outline" className="font-mono">{claim.marker}</Badge>
                <Badge variant="outline">{claim.category}</Badge>
                <Badge variant="outline" className={support.cls}>{support.text}</Badge>
                {claim.attached_pmid && (
                  <Badge variant="outline" className="bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30">
                    cited as @pmid:{claim.attached_pmid}
                  </Badge>
                )}
                {claim.status === 'stale' && (
                  <Badge variant="outline" className="bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/30">
                    Needs review — code changed
                  </Badge>
                )}
              </div>
              <p className="text-sm leading-relaxed break-words" data-testid="claim-source-text">{claim.claim_text}</p>
              {claim.support_note && (
                <p className="text-xs text-muted-foreground mt-2">{claim.support_note}</p>
              )}
              {claim.stale_reason && (
                <p className="text-xs text-orange-600 dark:text-orange-400 mt-2 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{claim.stale_reason}
                </p>
              )}
            </div>

            {usage.length > 0 && (
              <div className="rounded-lg border border-border p-3" data-testid="claim-usage-block">
                <p className="text-xs font-semibold text-muted-foreground mb-2">
                  Used in the manuscript ({usage.length})
                </p>
                {usage.map((u, i) => (
                  <div key={i} className="flex items-start gap-2 mb-2 last:mb-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{u.chapter_title}</p>
                      <p className="text-xs text-muted-foreground break-words">{u.excerpt.slice(0, 220)}…</p>
                    </div>
                    {onOpenChapter && (
                      <Button size="sm" variant="outline" data-testid={`claim-open-chapter-${i}`}
                        onClick={() => { onOpenChapter(u.chapter_id); onOpenChange(false); }}>
                        Open section
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {claim.support_type === 'needs_publication' && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-3" data-testid="claim-pubmed-block">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <p className="text-sm">
                    This statement applies domain knowledge the code cannot justify — it needs a published reference.
                  </p>
                  <Button size="sm" onClick={findReference} disabled={searching} data-testid="claim-find-reference-btn">
                    {searching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                    Find reference in PubMed
                  </Button>
                </div>

                {pubmed && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground font-mono break-words">
                      query: {pubmed.query}{pubmed.relaxed ? ' (relaxed automatically)' : ''}
                    </p>
                    {(pubmed.articles || []).map((a) => (
                      <div key={a.pmid} className="rounded-md border border-border bg-background p-2"
                        data-testid={`claim-pubmed-result-${a.pmid}`}>
                        <p className="text-sm font-medium break-words">{a.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {a.journal} · {a.year} · PMID {a.pmid}
                        </p>
                        <div className="flex gap-2 mt-2">
                          <Button size="sm" variant="outline" disabled={attaching === a.pmid}
                            onClick={() => attach(a.pmid)} data-testid={`claim-attach-${a.pmid}`}>
                            {attaching === a.pmid ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BookPlus className="w-4 h-4 mr-2" />}
                            Use as citation
                          </Button>
                          <a className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-1.5"
                            href={`https://pubmed.ncbi.nlm.nih.gov/${a.pmid}/`} target="_blank" rel="noreferrer">
                            PubMed <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      </div>
                    ))}
                    {!pubmed.count && (
                      <div className="rounded-md border border-border bg-background p-3" data-testid="claim-pubmed-empty">
                        <p className="text-sm font-medium">No PubMed results for this statement</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          The query was relaxed automatically and still returned nothing. Search PubMed
                          manually with your own wording, then import the article from the PubMed tab —
                          the citation token is <span className="font-mono">@pmid:&lt;id&gt;</span>.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {loading && <p className="text-sm text-muted-foreground">Loading source…</p>}
            {error && <p className="text-sm text-destructive">{error}</p>}

            {(data?.evidence || []).map((ev, i) => (
              <div key={i} className="rounded-lg border border-border overflow-hidden min-w-0" data-testid={`claim-evidence-${i}`}>
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/40 text-xs flex-wrap">
                  <span className="font-mono truncate">
                    {ev.file_path}
                    {ev.available && <> · L{ev.snippet_start_line}–{ev.snippet_end_line} of {ev.file_lines}</>}
                    {ev.symbol ? ` · ${ev.symbol}` : ''}
                  </span>
                  {ev.github_url && (
                    <a href={ev.github_url} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                      data-testid={`claim-evidence-github-${i}`}>
                      Open on GitHub <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                {ev.available ? (
                  <pre className="p-3 text-[11.5px] leading-relaxed overflow-auto max-h-72 whitespace-pre font-mono bg-background">
                    {ev.snippet}
                  </pre>
                ) : (
                  <p className="p-3 text-xs text-muted-foreground">{ev.message}</p>
                )}
              </div>
            ))}

            {data && (
              <p className="text-xs text-muted-foreground">
                Snapshot: {data.code_version ? `version ${data.code_version} · ` : ''}
                commit {data.commit_sha ? data.commit_sha.slice(0, 10) : 'n/a'}
              </p>
            )}
          </div>
        )}

        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="claim-source-close">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CodeClaimSourceDialog;
